<template>
  <div class="app-wrapper">
    <h1>v1 GENERAL TERMS AND CONDITIONS OF USE FOR MAPPR CONTENT CREATORS</h1>

    <p><strong>Last Updated 06 March 2025</strong></p>
    <p>
      These General Terms and Conditions of Use (hereinafter, the "Terms and
      Conditions") shall regulate the participation of content creators on the
      Mappr platform (hereinafter, "Mappr") and the use of its functionalities,
      including the rights and responsibilities related to the creation,
      publication, and monetization of geographic content.
    </p>

    <p>Mappr's details are as follows:</p>

    <ul>
      <li><strong>Company Name:</strong> Mapprexplore S.L.</li>
      <li>
        <strong>Registered Office:</strong> Plaça Vicens Martorell 2, PB L 2,
        08001, Barcelona, Spain
      </li>
      <li><strong>N.I.F. Number:</strong> B56479728</li>
      <li><strong>Email:</strong> info@mappr.com</li>
      <li>
        <strong>Mercantile Registry:</strong> Registered in the Mercantile
        Registry of Barcelona, Volume 48979, Folio 148, Sheet 603290.
      </li>
    </ul>

    <p>
      The main objective of the Mappr platform is to provide a space where
      content creators can share geographic recommendations, experiences, and
      other types of content related to destinations, restaurants, activities,
      etc. Users can access this content through a subscription, direct support,
      or free access.
    </p>

    <p>
      The conditions in force shall be those published at the time the content
      creator accesses and accepts the platform's Terms and Conditions.
    </p>

    <p>
      If any provision of these Terms and Conditions is deemed unenforceable or
      invalid, such provision shall be limited or removed to the minimum extent
      necessary so that the remaining terms and obligations remain in full force
      and effect and binding.
    </p>

    <p>
      Mappr may delete, modify, or replace the contents of this document at any
      time, publishing or sending a notification through the platform or via
      email, without the content creator having the right to claim any
      compensation.
    </p>

    <h2>1. ACCEPTANCE OF THE TERMS AND CONDITIONS</h2>

    <ol>
      <li>
        By registering and/or using the Platform, the Content Creator
        (hereinafter, the "Creator") accepts and agrees to comply with all the
        established Terms and Conditions. Any Creator has the possibility to
        register or use the Platform, provided they accept these Terms and
        Conditions, as well as all other policies and principles governing the
        operation of Mappr as detailed below.
      </li>

      <li>
        By accepting the Terms and Conditions, the Creator undertakes to always
        act in accordance with the provisions of this legal document and the
        applicable regulations. If the Creator disagrees, wholly or partially,
        with these Terms and Conditions, they must refrain from registering
        and/or using the Platform.
      </li>
      <li>
        The service offered on this Platform is subject to: (i) these General
        Terms and Conditions; (ii) Specific Conditions that may be published on
        the Platform, which may replace, supplement, and/or modify these Terms
        and Conditions; (iii) applicable laws, sectoral regulations, and general
        principles and practices.
      </li>

      <li>
        Mappr reserves the right to modify the presentation, configuration, and
        content of the Platform, as well as the conditions necessary for access
        and/or use. Access to and use of the content and services after such
        modifications or changes take effect implies acceptance of them.
      </li>
    </ol>

    <h2>2. USE OF THE PLATFORM</h2>

    <ol>
      <li>
        To register and contractually bind with Mappr, the Content Creator
        (hereinafter, the "Creator") declares they have the legal capacity to
        enter into a contract and acknowledges that they are legally authorized
        to act on their behalf or as a representative of an entity, in the case
        of registering an entity.
      </li>

      <li>
        These Terms and Conditions, as well as the Privacy Policies and any
        other information related to the Platform, apply to all Creators from
        the moment they access the Platform and/or begin their registration.
        These Terms and Conditions are mandatory, binding, and extend to all
        activities performed on the Platform.
      </li>

      <li>
        The Creator is responsible for ensuring that all individuals accessing
        the Platform through their internet connection, whether on their behalf
        or indirectly, are informed of and comply with the Terms and Conditions.
      </li>

      <li>
        The Creator acknowledges that their participation in the Platform does
        not generate any employment, commercial, or dependency relationship with
        Mappr. The relationship between Mappr and the Creator is independent,
        and no action carried out within the Platform shall be considered an
        employment relationship or subject to labor law provisions.
      </li>

      <li>
        The Creator has access to all necessary information related to content
        creation and management within the Platform, as well as the
        functionalities provided by Mappr for publishing, receiving payments,
        and managing their profile within the platform.
      </li>

      <li>
        The Creator agrees not to use the Platform for illegal purposes or
        effects or in violation of these Terms and Conditions. The Creator
        commits not to take any actions that could damage, interrupt, or
        overload the Platform or interfere with other Creators' or Users' access
        and use.
      </li>

      <li>
        The Creator agrees not to use the Platform to infringe on third-party
        intellectual property rights, including the creation of content that
        copies or uses materials without proper authorization. The Creator is
        solely responsible for ensuring that the content they upload to the
        Platform does not violate third-party intellectual property rights.
      </li>

      <li>
        The Creator is responsible for maintaining the confidentiality of their
        account and login credentials. In case of suspected unauthorized account
        use, the Creator must immediately notify Mappr to take appropriate
        measures. Mappr is not responsible for the improper use of registered
        accounts due to the Creator's negligence in protecting their
        credentials.
      </li>

      <li>
        Access to and use of the Platform with the Creator's password or
        credentials shall be deemed to be performed by the Creator, who will be
        responsible for all actions carried out under their account.
      </li>

      <li>
        Mappr reserves the right to modify the presentation, configuration, and
        content of the Platform, as well as the necessary conditions for access
        and/or use, to improve the service or adapt it to new needs. Access to
        and use of content and services after these modifications take effect
        implies acceptance of them.
      </li>

      <li>
        The Creator is responsible for ensuring that the information they
        provide on the Platform, including personal and tax data, is accurate
        and up to date at all times. Any changes in information must be
        communicated to Mappr for updating.
      </li>

      <li>
        Mappr reserves the right to reject or cancel any Creator's account that
        does not comply with the established requirements in these Terms and
        Conditions or who, in Mappr's judgment, is engaged in fraudulent,
        illegal, or Platform-violating activities.
      </li>

      <li>
        The Creator agrees not to engage in or allow third parties to engage in
        fraudulent activities through their account, such as misusing the
        Platform for money laundering, identity theft, or any other illicit
        activity.
      </li>
    </ol>

    <h2>3. REGISTRATION ON THE PLATFORM</h2>

    <ol>
      <li>
        To access Mappr's services, the Creator must complete an online
        registration form, providing the necessary information requested by the
        Platform. This includes, among others, full name, email, and, if
        applicable, tax identification or company details in the case of entity
        registration.
      </li>

      <li>
        The Creator guarantees that all information provided during registration
        is accurate, truthful, and complete. If any provided data changes, the
        Creator commits to updating such information immediately through the
        Platform.
      </li>

      <li>
        To complete registration, the Creator must provide requested
        information, such as email, tax identification number, company or
        freelancer name (if applicable), and any other data Mappr deems
        necessary for account validation. Once the form is completed, Mappr will
        contact the Creator to finalize the validation and approval process.
      </li>

      <li>
        Mappr cannot guarantee the identity of all registered Creators on the
        Platform and assumes no responsibility for the misuse of a registered
        Creator's account by third parties who have obtained unauthorized
        access.
      </li>

      <li>
        The Creator declares that they possess the necessary authorizations
        under applicable laws to offer their services through the Platform and
        that they are legally authorized to upload and distribute content
        without violating any law or third-party intellectual property rights.
      </li>

      <li>
        The Creator is responsible for maintaining the confidentiality of their
        access password and any other security-related information linked to
        their account. The Creator commits to notifying Mappr immediately of any
        unauthorized account use or security breaches.
      </li>

      <li>
        Access to and use of the Platform with the Creator's password shall be
        deemed to be performed by the Creator. Mappr is not responsible for the
        misuse of the account when such misuse was not authorized by the
        Creator.
      </li>

      <li>
        Mappr reserves the right to reject registration requests or cancel
        previously accepted registrations without needing to communicate the
        reasons, without generating any right to compensation.
      </li>
    </ol>
    <h2>4. OBLIGATIONS AND RESPONSIBILITIES OF THE CREATOR</h2>

    <ol>
      <li>
        The Creator commits to creating, publishing, and maintaining original
        and high-quality content on the Platform. All content must comply with
        applicable laws and must not infringe third- party intellectual property
        rights.
      </li>

      <li>
        The Creator is solely responsible for the content they upload to the
        Platform, including any text, images, videos, links, or other materials.
        Mappr is not responsible for the accuracy, legality, integrity, or
        authenticity of the content provided by the Creator.
      </li>

      <li>
        The Creator agrees not to upload content that may be considered illegal,
        defamatory, obscene, offensive, invasive of third-party privacy,
        infringing on intellectual property rights, or in any way contrary to
        the provisions of these Terms and Conditions.
      </li>

      <li>
        The Creator is responsible for ensuring that all content uploaded to the
        Platform is suitable for public distribution and does not violate any
        law, regulation, or third-party rights.
      </li>

      <li>
        The Creator agrees not to use the Platform for commercial purposes
        unauthorized by Mappr, such as promoting products or services unrelated
        to the Platform without Mappr’s prior written consent.
      </li>

      <li>
        The Creator agrees to notify Mappr immediately if they suspect their
        account has been compromised or if they detect any unauthorized activity
        in their account.
      </li>

      <li>
        The Creator is responsible for all aspects related to their account,
        including the security of their login credentials, content publication,
        and interactions with users. Mappr will not be responsible for
        unauthorized or inappropriate use of the Creator’s account.
      </li>

      <li>
        The Creator agrees to cooperate with Mappr if investigations related to
        fraudulent activities or violations of the Platform’s Terms and
        Conditions are initiated.
      </li>
    </ol>

    <h2>5. MONETIZATION AND PAYMENTS</h2>

    <ol>
      <li>
        The Creator may generate revenue through the Mappr platform using
        various available monetization models, such as subscriptions, direct
        supporter contributions, affiliate marketing, and advertising within
        their content.
      </li>

      <li>
        <strong>Advances and Content Promotion:</strong> On certain occasions
        and under specific conditions, upon joining Mappr, the Creator may
        receive an available balance that they can withdraw immediately. This
        advance does not apply to all Creators, only to those who meet the
        specific criteria set by Mappr.
      </li>

      <li>
        In certain cases, Mappr may require the Creator to promote their content
        for an initial period of one (1) month before proceeding with payment.
      </li>

      <li>
        Once this period ends, Mappr will evaluate the performance of the
        Creator’s content and estimate their annual earnings.
      </li>

      <li>
        Based on this estimate, Mappr may offer the Creator an advance payment.
        This amount will later be deducted from the revenue generated through
        the Platform until it has been fully covered.
      </li>

      <li>
        The Creator expressly agrees that any advance received from Mappr will
        be considered a prepayment on future earnings, and repayment will be
        made through automatic compensation with revenue generated on the
        Platform.
      </li>

      <li>
        <strong>Revenue Distribution:</strong> The revenue generated by the
        Creator through subscriptions and supporter contributions will be
        distributed at most on a monthly basis, according to the percentage
        established in these Terms and Conditions and Mappr’s policies. However,
        in some specific cases, revenue distribution may be processed
        automatically on a daily basis.
      </li>

      <li>
        Mappr reserves the right to retain a percentage of the Creator’s
        generated revenue, which will be specified in the terms of each
        monetization model. Payments will be processed through Mappr’s
        designated payment platforms, such as Stripe, and will be subject to the
        fees and charges of these services
      </li>

      <li>
        <strong>Payment Requirements:</strong> To receive payments, the Creator
        must provide their tax and banking information to Mappr. The Creator is
        responsible for ensuring that this information is accurate and up to
        date.
      </li>

      <li>
        Payments to Creators will be made on a monthly basis or according to the
        payment period determined by Mappr and will be subject to a minimum
        income threshold established by Mappr. If the Creator does not reach the
        minimum threshold, the revenue will be accumulated until it reaches the
        required amount in the next payment cycle.
      </li>

      <li>
        <strong>Taxes and Fiscal Obligations:</strong> The Creator is
        responsible for complying with all tax obligations related to the
        revenue generated on the Platform, including but not limited to income
        declaration and tax payments as required by the laws of their country of
        residence.
      </li>

      <li>
        Mappr will not be responsible for any tax obligations or liabilities of
        the Creator related to payments made through the Platform.
      </li>

      <li>
        The Creator can review their accumulated earnings and payment status
        through the Platform in the corresponding section of their account.
      </li>

      <li>
        Mappr reserves the right to modify payment conditions and revenue
        distribution fees, notifying the Creator with reasonable advance notice
        of any changes to these policies.
      </li>
    </ol>

    <h2>6. INTELLECTUAL PROPERTY</h2>

    <ol>
      <li>
        The Creator retains all intellectual property rights over the content
        they upload to the Platform, including, but not limited to, texts,
        images, videos, maps, links, and any other material. However, by
        uploading content to Mappr, the Creator grants Mappr a non-exclusive,
        worldwide, royalty-free, and transferable license to use, display,
        distribute, and promote such content within the Platform and in related
        marketing materials.
      </li>

      <li>
        The license granted by the Creator to Mappr remains valid while the
        content is available on the Platform. This license allows Mappr to use
        the content in all types of media, including digital platforms and
        social networks, for promotional and advertising purposes without
        requiring additional authorization from the Creator.
      </li>

      <li>
        The Creator may request that Mappr remove their content from marketing
        materials if they do not wish for their content to be used for
        promotional purposes outside the Platform. The removal request must be
        submitted in writing, and Mappr agrees to cease using the content in
        marketing materials within a reasonable timeframe, provided the content
        has not already been used in prior advertising campaigns.
      </li>

      <li>
        The Creator guarantees that the content they upload to the Platform is
        original and does not infringe third-party intellectual property rights.
        The Creator is solely responsible for any claims related to intellectual
        property rights violations arising from their content.
      </li>

      <li>
        In the event that Mappr receives a claim regarding intellectual property
        rights infringement or any other legal matter concerning the content
        published by the Creator, the Creator shall be solely responsible for
        resolving such a claim and assuming any resulting legal consequences.
      </li>

      <li>
        The Creator agrees to indemnify and hold Mappr harmless from any loss,
        damage, cost, or expense, including reasonable legal fees, arising from
        the Creator’s infringement of third-party intellectual property rights.
      </li>
    </ol>

    <h2>7. LIABILITY AND DISCLAIMER</h2>

    <ol>
      <li>
        The Creator is responsible for all content they upload to the Platform
        and for interactions with other users. Mappr is not responsible for the
        content uploaded by the Creator, nor for the truthfulness, quality, or
        accuracy of such content.
      </li>

      <li>
        Mappr does not guarantee that the Platform will be free of errors,
        interruptions, or defects and shall not be liable for any damage or loss
        resulting from the use of the Platform or the inability to access it.
      </li>

      <li>
        Mappr shall not be liable for any direct, indirect, incidental, special,
        or consequential damages that may arise as a result of using the
        Platform, even if Mappr has been notified of the possibility of such
        damages.
      </li>

      <li>
        Mappr is not responsible for the conduct of Creators or Users within the
        Platform. The Creator assumes full responsibility for their own conduct,
        as well as for any illegal or inappropriate behavior that may arise from
        their participation in the Platform.
      </li>

      <li>
        Mappr shall not be liable for the unauthorized or improper use of
        Creators’ accounts and shall not be responsible for losses or damages
        resulting from fraudulent use of the Creator’s login credentials.
      </li>

      <li>
        The Creator agrees to indemnify and hold Mappr harmless from any claim,
        demand, loss, damage, liability, cost, or expense, including legal fees,
        arising from their use of the Platform or their breach of these Terms
        and Conditions.
      </li>

      <li>
        Mappr shall not be responsible for payments not processed correctly for
        services provided through the Platform if such failures are due to
        issues with payment service providers or technical errors beyond Mappr’s
        control.
      </li>

      <li>
        The Creator acknowledges that, under no circumstances, shall Mappr be
        held responsible for lost revenue or profits, nor for damages resulting
        from the unavailability of the Platform or service interruptions.
      </li>
    </ol>

    <h2>8. PRIVACY AND DATA PROTECTION</h2>

    <ol>
      <li>
        The Creator authorizes Mappr to process their personal data in
        accordance with applicable data protection laws, including data provided
        during the registration process and any other data collected in
        connection with the use of the Platform.
      </li>

      <li>
        Mappr commits to protecting the Creator’s privacy and confidentiality
        and will use such data solely for purposes related to the provision of
        services offered through the Platform, as well as to comply with legal
        or regulatory obligations.
      </li>

      <li>
        The Creator consents to the use of their personal data for
        communications related to their account, such as operational
        notifications, Platform updates, and commercial communications, unless
        they have expressed their desire not to receive such communications.
      </li>

      <li>
        The Creator has the right to access, rectify, cancel, and object to the
        processing of their personal data at any time, in accordance with
        applicable data protection regulations. To exercise these rights, the
        Creator must contact Mappr through the means indicated in the Privacy
        Policy.
      </li>

      <li>
        Mappr will implement the necessary technical and organizational security
        measures to protect the Creator’s personal data against unauthorized
        access, alteration, disclosure, or destruction.
      </li>

      <li>
        The Creator acknowledges that the use of the Platform may involve the
        international transfer of personal data. Mappr commits to ensuring that
        such transfers comply with applicable data protection regulations.
      </li>

      <li>
        The Creator’s personal data may be shared with service providers and
        business partners acting on behalf of Mappr, provided that these
        providers commit to complying with applicable data protection laws and
        using the data exclusively for the purposes established by Mappr.
      </li>
    </ol>

    <h2>9. MODIFICATIONS AND TERMINATION</h2>

    <ol>
      <li>
        <strong>Modification of the Terms and Conditions:</strong> Mappr
        reserves the right to modify these Terms and Conditions at any time. Any
        changes will be notified to the Creator through the Platform or via
        email, and such changes will become effective upon publication.
        Continued use of the Platform after the modification of the Terms and
        Conditions implies acceptance of the changes.
      </li>

      <li>
        <strong>Suspension or Termination of the Creator’s Access:</strong>
        Mappr may suspend or cancel any Creator’s access to the Platform if a
        breach of these Terms and Conditions is detected or if the Creator
        engages in activities that Mappr considers inappropriate or illegal. In
        the event of suspension or cancellation, the Creator will not be
        entitled to any compensation.
      </li>

      <li>
        <strong>Voluntary Termination:</strong> The Creator may terminate their
        relationship with Mappr at any time by notifying their decision through
        the means indicated on the Platform. In the event of voluntary
        termination, the Creator will lose access to all services on the
        Platform, including any revenue generated, if the necessary payment
        requirements have not been met.
      </li>

      <li>
        <strong>Obligations After Termination:</strong> Upon termination of the
        Creator’s access to the Platform, Mappr may withhold any outstanding
        payments to the Creator until any pending claims, liabilities, or
        payments are resolved. The Creator will remain responsible for any
        content published before termination, and Mappr may continue using such
        content under the previously agreed terms.
      </li>

      <li>
        <strong>Duration:</strong> These Terms and Conditions shall apply for as
        long as the Creator uses the Platform. The relationship between Mappr
        and the Creator may be terminated by either party at any time, in
        accordance with these Terms and Conditions.
      </li>

      <li>
        <strong>Effects of Termination :</strong> In the event of termination of
        the relationship, regardless of the cause, the provisions that by their
        nature should survive termination, such as those related to intellectual
        property, liability, data protection, and payment obligations, shall
        remain in force.
      </li>
    </ol>

    <h2>10. APPLICABLE LAW AND JURISDICTION</h2>

    <ol>
      <li>
        These Terms and Conditions shall be governed by and interpreted in
        accordance with the laws in force in Spain.
      </li>

      <li>
        In the event of any conflict, doubt, or discrepancy arising from the
        interpretation, execution, or compliance with these Terms and
        Conditions, the parties agree to submit to the competent courts of the
        city of Barcelona, Spain, expressly waiving any other jurisdiction that
        may apply to them.
      </li>

      <li>
        Notwithstanding the above, if applicable law allows, Mappr may opt to
        resolve any dispute through arbitration or mediation instead of
        resorting to the courts, in accordance with the alternative dispute
        resolution regulations available in Spain.
      </li>

      <li>
        If any provision of these Terms and Conditions is declared null,
        illegal, or unenforceable by a competent court, such provision shall be
        modified to the minimum extent necessary to ensure that the remaining
        provisions remain valid and enforceable.
      </li>
    </ol>

    <h2>11. NOTIFICATIONS</h2>

    <ol>
      <li>
        Any notice or communication related to these Terms and Conditions shall
        be made in writing and sent to the email address provided by the Creator
        during the registration process or to any other address subsequently
        provided by them.
      </li>

      <li>
        Any notifications sent by Mappr to the Creator shall be considered valid
        if sent to the email address registered in their account. It is the
        Creator’s responsibility to ensure that their contact information is
        updated at all times
      </li>

      <li>
        The Creator may send notifications to Mappr through the contact channels
        available on the Platform or via email at info@mappr.com. Any
        communication directed to Mappr shall be deemed received once Mappr
        confirms receipt.
      </li>

      <li>
        Notifications shall be considered effective upon their dispatch unless a
        different timeframe for effectiveness is specified.
      </li>
    </ol>
  </div>
</template>
